
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
export class InpatientService {
  // Static methods for all functionality
  static async getInpatientsByFacility(facilityId, limitCount = 100) {
    try {
      const token = await getAuthToken();
      // Use the correct endpoint that exists
      const response = await axios.get(
        `${API_BASE}/clinical/encounters/inpatient?facilityId=${facilityId}&limit=${limitCount}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.encounters || [];
    } catch (error) {
      console.error('Error fetching inpatients:', error);
      throw error;
    }
  }

  static async getPatientData(patientId) {
    try {
      const { getPatient } = await import('./patientService');
      const patient = await getPatient(patientId);
      return patient || {};
    } catch (error) {
      console.error('Error fetching patient data:', error);
      throw error;
    }
  }

  static async getPatientsData(patientIds) {
    try {
      const { getPatient } = await import('./patientService');
      const patients = await Promise.all(
        patientIds.map(async (id) => {
          try {
            return await getPatient(id);
          } catch (error) {
            console.warn(`Failed to fetch patient ${id}:`, error);
            return null;
          }
        })
      );
      return patients.filter(Boolean);
    } catch (error) {
      console.error('Error fetching patients data:', error);
      throw error;
    }
  }

  // Note: Listener functionality removed - use refresh button instead
  // This simplifies the architecture and improves reliability

  // Static method for PatientTransfer.js
  static async getInpatientEncounters(facilityId) {
    try {
      if (!facilityId) {
        console.warn('No facility ID provided to getInpatientEncounters, returning empty array');
        return { encounters: [] };
      }

      const token = await getAuthToken();
      console.log('Fetching inpatient encounters for facility:', facilityId);
      
      const response = await axios.get(
        `${API_BASE}/clinical/encounters/inpatient?facilityId=${facilityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Inpatient encounters response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching inpatient encounters:', error);
      if (error.response?.status === 404) {
        console.log('Inpatient endpoint not found, returning empty data');
        return { encounters: [] };
      }
      throw error;
    }
  }

  static async getTransferHistory(encounterId) {
    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${API_BASE}/clinical/encounters/${encounterId}/transfer-history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching transfer history:', error);
      // Return empty data if endpoint doesn't exist yet
      return { transferHistory: [] };
    }
  }

  static async getFacilityDepartments(facilityId) {
    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${API_BASE}/facility/facilities/${encodeURIComponent(facilityId)}/departments`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching facility departments:', error);
      // Return empty data if endpoint doesn't exist yet
      return { departments: [] };
    }
  }

  static async createTransferRecord(encounterId, transferData) {
    try {
      const token = await getAuthToken();
      const response = await axios.post(
        `${API_BASE}/clinical/transfers`,
        {
          encounterId,
          ...transferData
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error creating transfer record:', error);
      throw error;
    }
  }

  static async updatePatient(patientId, patientData) {
    try {
      const { updatePatient: updatePatientFn } = await import('./patientService');
      return await updatePatientFn(patientId, patientData);
    } catch (error) {
      console.error('Error updating patient:', error);
      throw error;
    }
  }

  static async updateEncounterStatus(encounterId, status, additionalData = {}) {
    try {
      const token = await getAuthToken();
      const response = await axios.put(
        `${API_BASE}/clinical/encounters/${encounterId}/status`,
        {
          status,
          ...additionalData
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error updating encounter status:', error);
      throw error;
    }
  }
}
